import axios from 'axios';
import Endpoint from '../Endpoint';

/**
 * Logger centralizado para el frontend
 * Envía logs al backend que los sube a CloudWatch
 */
class Logger {
    constructor() {
        this.enabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_LOGGING === 'true';
        this.queue = [];
        this.flushInterval = null;
        this.maxQueueSize = 20;
        this.flushDelay = 5000; // 5 segundos
        
        // Iniciar flush automático
        if (this.enabled) {
            this.startAutoFlush();
        }
        
        // Capturar errores no manejados
        this.setupGlobalErrorHandlers();
    }

    setupGlobalErrorHandlers() {
        // Ya están configurados en App.js, pero podemos mejorarlos aquí
        if (typeof window !== 'undefined') {
            const originalError = window.onerror;
            window.onerror = (message, source, lineno, colno, error) => {
                this.error('Error global no manejado', {
                    message: String(message),
                    source,
                    lineno,
                    colno,
                    error: error ? {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    } : null,
                });
                
                // Llamar al handler original si existe
                if (originalError) {
                    return originalError(message, source, lineno, colno, error);
                }
                return false;
            };

            const originalRejection = window.onunhandledrejection;
            window.addEventListener('unhandledrejection', (event) => {
                this.error('Promise rejection no manejada', {
                    reason: event.reason ? String(event.reason) : 'Unknown',
                    error: event.reason instanceof Error ? {
                        name: event.reason.name,
                        message: event.reason.message,
                        stack: event.reason.stack,
                    } : null,
                });
                
                if (originalRejection) {
                    originalRejection(event);
                }
            });
        }
    }

    startAutoFlush() {
        if (this.flushInterval) {
            return;
        }
        
        this.flushInterval = setInterval(() => {
            this.flush();
        }, this.flushDelay);
    }

    stopAutoFlush() {
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }
    }

    getContext() {
        return {
            userAgent: navigator.userAgent,
            url: window.location.href,
            pathname: window.location.pathname,
            timestamp: new Date().toISOString(),
            userId: this.getUserId(),
            screen: {
                width: window.screen?.width,
                height: window.screen?.height,
            },
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
        };
    }

    getUserId() {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                // Decodificar JWT para obtener userId
                const payload = JSON.parse(atob(token.split('.')[1]));
                return payload.id || null;
            }
        } catch (error) {
            // Silenciosamente fallar
        }
        return null;
    }

    async log(level, message, metadata = {}) {
        // Siempre loguear a consola en desarrollo
        if (process.env.NODE_ENV === 'development') {
            const consoleMethod = level === 'error' ? console.error : 
                                 level === 'warn' ? console.warn : 
                                 level === 'info' ? console.info : 
                                 console.log;
            consoleMethod(`[${level.toUpperCase()}] ${message}`, metadata);
        }

        // Si está deshabilitado, no enviar al servidor
        if (!this.enabled) {
            return;
        }

        const logEntry = {
            level,
            message,
            metadata: {
                ...this.getContext(),
                ...metadata,
            },
            timestamp: new Date().toISOString(),
        };

        // Agregar a la cola
        this.queue.push(logEntry);

        // Si la cola está llena, enviar inmediatamente
        if (this.queue.length >= this.maxQueueSize) {
            await this.flush();
        }
    }

    async flush() {
        if (this.queue.length === 0) {
            return;
        }

        const logsToSend = [...this.queue];
        this.queue = [];

        try {
            // Enviar logs en batch
            await Promise.all(
                logsToSend.map(log => 
                    axios.post(`${Endpoint}/api/log`, log, {
                        timeout: 3000, // Timeout corto para no bloquear
                    }).catch(err => {
                        // Silenciosamente fallar, no queremos que los logs rompan la app
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('[Logger] Error enviando log:', err.message);
                        }
                    })
                )
            );
        } catch (error) {
            // Si falla, reintentar más tarde (volver a la cola)
            this.queue.unshift(...logsToSend);
            if (process.env.NODE_ENV === 'development') {
                console.warn('[Logger] Error en flush, reintentando más tarde');
            }
        }
    }

    async info(message, metadata = {}) {
        await this.log('info', message, metadata);
    }

    async warn(message, metadata = {}) {
        await this.log('warn', message, metadata);
    }

    async error(message, metadata = {}) {
        await this.log('error', message, metadata);
    }

    async debug(message, metadata = {}) {
        if (process.env.NODE_ENV === 'development') {
            await this.log('debug', message, metadata);
        }
    }

    // Método para enviar errores de React Error Boundary
    async reactError(error, errorInfo) {
        await this.error('Error de React Error Boundary', {
            error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
            },
            componentStack: errorInfo?.componentStack,
        });
    }

    // Método para enviar errores de API
    async apiError(endpoint, error, requestData = {}) {
        await this.error('Error en llamada API', {
            endpoint,
            error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                response: error?.response ? {
                    status: error.response.status,
                    data: error.response.data,
                } : null,
            },
            request: requestData,
        });
    }
}

// Exportar instancia singleton
const logger = new Logger();

// Forzar flush antes de que la página se cierre
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        logger.stopAutoFlush();
        logger.flush();
    });
}

export default logger;
